import DPO from 'dpo8core';
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import LocalizedText from '@LocalizedText';
import { parseATBCodesFile, setATBCodesParsedData, clearATBCodesParsedData } from '@app/actions/applyCodesActions';
import { APPLY_CODES_FLOW_SELECTION } from '@app/constants';
import { setFooterConfig } from '@app/actions/footerActions';
import { getFileExt, isMobileMode } from '@app/utils/utils';
import { inject } from '@app/providers/QAU.ContextProvider';
import { addError } from '@app/actions/popupsActions';

import UploadCodesFinish from './upload-codes-finish/UploadCodesFinish';
import DropZone from '@components/drop-zone/DropZone';
import FileInput from '@components/buttons/file-input-btn/FileInput';
import './UploadCodes.scss';
import useIntlMessage from '@app/hooks/use-intl-message';

const supportedCodesFileTypes = ['.csv', '.json'];

const ChooseFile = ({ isMobileView, resetSelectedFile, onFileUploaded, dropZoneTitle }) =>
  isMobileView ? (
    <FileInput
      handleFileSelect={file => {
        resetSelectedFile();
        onFileUploaded(file);
      }}
      locKey="qau-apply-codes-choose-file"
      accept={supportedCodesFileTypes.join(',')}
    />
  ) : (
    <DropZone
      titleLocKey="qau-apply-codes-drop-file"
      fileBtnLocKey="qau-apply-codes-choose-file"
      supportedTypes={supportedCodesFileTypes.join(',')}
      resetSelectedFile={resetSelectedFile}
      handleFileSelect={onFileUploaded}
      dropZoneTitle={dropZoneTitle}
    />
  );

const UploadCodes = ({
  clearATBCodesParsedData,
  setATBCodesParsedData,
  selectApplyCodesStep,
  parseATBCodesFile,
  setFooterConfig,
  updateContext,
  ATBCodesData,
  addError,
  context,
  flow,
  ctx,
}) => {
  const [showFinishStep, setShowFinishStep] = useState(false);
  const [dropZoneTitle] = useIntlMessage('qau-apply-codes-drop-file');
  const isMobileView = isMobileMode(ctx);

  const handleFileParsed = useCallback(
    ATBCodesData => {
      const { projectData } = flow;
      projectData.mailMergeData = ATBCodesData;
      projectData.mailMergeData.hint.push({ name: 'barcodeType', value: 'UPC-A' });
      projectData.mailMergeData.name = 'ATB Over Labeling';

      const mmData = DPO.mmd.Data.fromObject(projectData.mailMergeData);
      const provider = new DPO.mmd.DataSourceProvider(mmData);

      const newContext = context.createNew(flow.project, projectData, provider);
      newContext.storage.imageType = projectData.product.productProfile === 'DEFAULT' ? 'low' : 'cmyk';

      updateContext(newContext);
      setShowFinishStep(true);
    },
    [flow, context, updateContext],
  );

  const parseCodesFile = file => {
    const { projectData } = context;
    const formData = new FormData();

    formData.append('atbCodesFile', file, file.name);
    parseATBCodesFile(formData, projectData).then(setATBCodesParsedData).catch(showUploadCodesError);
  };

  const onFileUploaded = file => {
    const fileExtension = getFileExt(file);
    const extensionInvalid = !supportedCodesFileTypes.includes(`.${fileExtension}`);

    extensionInvalid ? showUploadCodesError() : parseCodesFile(file);
  };

  const showUploadCodesError = (errors = []) => {
    const [error = {}] = Array.isArray(errors) ? errors : [errors];
    const { code } = error;

    let errorLocKey = 'qau-upload-codes-wrong-file-type';

    if (code === 441) {
      errorLocKey = 'qau-upload-codes-no-transparency-codes';
    }

    if ([500, 600].includes(code)) {
      errorLocKey = 'qau-upload-codes-something-went-wrong';
    }

    if (code === 465) {
      errorLocKey = 'qau-mixed-content-error';
    }

    addError(errorLocKey);
  };

  const resetSelectedFile = useCallback(() => {
    clearATBCodesParsedData();
    setShowFinishStep(false);
  }, [clearATBCodesParsedData]);

  const returnToApplyCodesFlowSelection = useCallback(() => {
    selectApplyCodesStep(APPLY_CODES_FLOW_SELECTION);
  }, [selectApplyCodesStep]);

  useEffect(() => {
    setFooterConfig({
      visible: true,
      nextHidden: true,
      backHidden: false,
      onBack: returnToApplyCodesFlowSelection,
    });
  }, [setFooterConfig, showFinishStep, returnToApplyCodesFlowSelection]);

  useEffect(() => {
    ATBCodesData && !showFinishStep && handleFileParsed(ATBCodesData);
  }, [ATBCodesData, showFinishStep, handleFileParsed]);

  return (
    <div id="qau-upload-codes">
      <div className="upload-codes-title">
        <LocalizedText id="qau-apply-codes-upload-title" />
      </div>
      <div className="upload-codes-desc">
        <LocalizedText id={`qau-apply-codes-upload-${showFinishStep ? 'success' : 'desc'}`} />
      </div>
      {showFinishStep ? (
        <UploadCodesFinish resetSelectedFile={resetSelectedFile} />
      ) : (
        <ChooseFile
          isMobileView={isMobileView}
          resetSelectedFile={resetSelectedFile}
          onFileUploaded={onFileUploaded}
          dropZoneTitle={dropZoneTitle}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ applyCodes: { ATBCodesData }, matrix: { ctx } }) => ({ ATBCodesData, ctx });

const mapDispatchToProps = {
  clearATBCodesParsedData,
  setATBCodesParsedData,
  parseATBCodesFile,
  setFooterConfig,
  addError,
};

ChooseFile.propTypes = {
  isMobileView: PropTypes.bool.isRequired,
  resetSelectedFile: PropTypes.func.isRequired,
  onFileUploaded: PropTypes.func.isRequired,
  dropZoneTitle: PropTypes.string,
};

UploadCodes.propTypes = {
  ATBCodesData: PropTypes.object,
  flow: PropTypes.object.isRequired,
  ctx: PropTypes.object.isRequired,
  addError: PropTypes.func.isRequired,
  context: PropTypes.object.isRequired,
  updateContext: PropTypes.func.isRequired,
  setFooterConfig: PropTypes.func.isRequired,
  parseATBCodesFile: PropTypes.func.isRequired,
  selectApplyCodesStep: PropTypes.func.isRequired,
  setATBCodesParsedData: PropTypes.func.isRequired,
  clearATBCodesParsedData: PropTypes.func.isRequired,
};

export default inject('flow', 'context', 'updateContext')(connect(mapStateToProps, mapDispatchToProps)(UploadCodes));
